import { KeyboardArrowDown } from "@mui/icons-material";
import { Autocomplete, Button, styled, TextField } from "@mui/material";
import { useEffect, useState } from "react";
import useDebounce from "../../../hooks/use-debounce";
import { IListDto } from "../../../interface/dto/list.dto";
import { useAppDispatch, useAppSelector } from "../../../redux/store";
import { IWithClassName, IWithThemeProps } from "../../../theme/default.theme";
import {
    trackListSearchAction,
    trackListSelectAction,
} from "../redux/list-to-track/list-to-track.actions";
import { trackedListTrackAction } from "../redux/tracked-lists.actions";

const ListToTrackSearchBar = ({ className }: IWithClassName) => {
    const [searchText, setSearchText] = useState<string | null>(null);

    const debouncedValue = useDebounce(searchText);
    const dispatch = useAppDispatch();

    const { isLoading, selectedList, lists } = useAppSelector(
        (state) => state.trackedList.listToTrack,
    );

    useEffect(() => {
        dispatch(trackListSearchAction(debouncedValue ?? ""));
    }, [debouncedValue]);

    const enterKeyDownHandler = () => {
        const filteredList = lists.find(
            ({ name, hasAccess }) =>
                hasAccess && name?.toLowerCase() === searchText?.toLowerCase(),
        );
        changeHandler(filteredList);
    };

    const changeHandler = (value: IListDto | null | string | undefined) => {
        if (typeof value === "object" && value?.listId) {
            dispatch(trackListSelectAction(value));
        }
    };

    const clearAutocomplete = () => {
        setSearchText("");
        dispatch(trackListSelectAction(undefined));
    };

    const saveInTrackedList = () => {
        if (selectedList?.listId) {
            dispatch(trackedListTrackAction(selectedList.listId));
            clearAutocomplete();
        }
    };

    return (
        <div className={className}>
            <Autocomplete
                freeSolo
                forcePopupIcon
                options={lists}
                getOptionLabel={(list) =>
                    typeof list === "object" ? list.name : list
                }
                onChange={(event, value) => changeHandler(value)}
                popupIcon={<KeyboardArrowDown className="icon" />}
                loading={isLoading}
                componentsProps={{
                    clearIndicator: {
                        onClick: () => setSearchText(""),
                    },
                }}
                value={searchText}
                renderInput={(params) => (
                    <>
                        <TextField
                            {...params}
                            value={searchText}
                            onChange={(event) =>
                                setSearchText(event.target.value)
                            }
                            variant="standard"
                            onFocus={() => clearAutocomplete()}
                            placeholder="Select an Option"
                            onKeyDown={(event) =>
                                event.code === "Enter" && enterKeyDownHandler()
                            }
                        />
                    </>
                )}
            />
            <Button
                variant="outlined"
                color="secondary"
                className="add-button"
                onClick={saveInTrackedList}
            >
                Add
            </Button>
        </div>
    );
};

export default styled(ListToTrackSearchBar)(({ theme }: IWithThemeProps) => ({
    display: "flex",
    ".MuiAutocomplete-root": {
        width: 200,

        "& svg": {
            color: theme?.palette.history.textColor,
        },
        "& .MuiAutocomplete-clearIndicator": {
            right: "unset",
        },
    },
    ".add-button": {
        height: "2.3em",
    },
    ".MuiAutocomplete-root.Mui-focused .MuiTextField-root": {
        backgroundColor: theme?.palette.search.iconColor,
    },
}));
